import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  Divider,
  CircularProgress,
  Chip,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LoopIcon from "@mui/icons-material/Loop";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import { useAlerts } from "../../contexts/AlertContext";
import { useApp } from "../../hooks/useApp";
import { useSidebarData } from "../../pages/global/SidebarDataContext";
import { markEmailsSeen } from "../../services/email/EmailHistoryService";
import { useNavigate, useLocation } from "react-router-dom";

const getPriorityColor = (priority) => {
  switch (priority) {
    case "HIGH":
      return "#f44336";
    case "MEDIUM":
      return "#ff9800";
    case "LOW":
      return "#2196f3";
    default:
      return "#9e9e9e";
  }
};

const getPriorityIcon = (priority, alertType) => {
  if (alertType === "NURTURE_CYCLE") {
    return <LoopIcon sx={{ color: "#4caf50", fontSize: 20 }} />;
  }
  if (alertType === "REGRESSION") {
    return <SwapHorizIcon sx={{ color: "#f44336", fontSize: 20 }} />;
  }
  switch (priority) {
    case "HIGH":
      return <ErrorOutlineIcon sx={{ color: "#f44336", fontSize: 20 }} />;
    case "MEDIUM":
      return <WarningAmberIcon sx={{ color: "#ff9800", fontSize: 20 }} />;
    case "LOW":
      return <InfoOutlinedIcon sx={{ color: "#2196f3", fontSize: 20 }} />;
    default:
      return <InfoOutlinedIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />;
  }
};

const timeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return "Hace un momento";
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Hace ${Math.floor(diff / 86400)}d`;
  return date.toLocaleDateString("es-CR");
};

const NotificationPanel = ({ anchorEl, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    alerts,
    unreadCount,
    loading,
    totalPages,
    page,
    loadAlerts,
    loadMore,
    markAsRead,
    markAllRead,
    dismiss,
  } = useAlerts();
  const { messageUser, proposalNotifications, removeProposalNotification } = useApp();
  const { emailUnreadMap, emailUnreadTotal, refreshEmailUnread } = useSidebarData();

  const topUnreadLeadId = Object.entries(emailUnreadMap || {})
    .sort(([, a], [, b]) => b - a)
    .map(([id]) => Number(id))[0] || null;

  // Mantiene los items de mensajes que el usuario ya clickeó (leídos)
  const readMessagesRef = useRef({});

  // Mezcla mensajes no leídos + leídos recientemente (para que persistan en el panel)
  const allMessageNotifications = useMemo(() => {
    const unreadMap = new Map(messageUser.map((i) => [i.client.id, i]));

    // Solo muestra los leídos del ref si ya NO están en messageUser (fueron marcados como leídos en el chat)
    const readItems = Object.values(readMessagesRef.current).map((i) => ({
      ...i,
      countNewMessages: 0,
      isRead: true,
    }));

    return [
      ...messageUser.map((i) => ({ ...i, isRead: false })),
      ...readItems.filter((i) => !unreadMap.has(i.client.id)),
    ];
  }, [messageUser]);

  // Solo cuenta los no leídos para el badge
  const newMessagesCount = messageUser.reduce(
    (sum, item) => sum + (item.countNewMessages || 0),
    0
  );

  const open = Boolean(anchorEl);

  const handleOpen = () => {
    loadAlerts(0);
  };

  const handleOpenChat = (item) => {
    // Guardar en ref como "leído" para que persista en el panel
    if (!item.isRead) {
      readMessagesRef.current[item.client.id] = item;
    }
    onClose();
    const clientData = {
      id: item.client.id,
      name: item.client.name,
      cellPhone: item.client.cellPhone,
      prospect: item.client.prospect,
    };
    localStorage.setItem("selectedClient", JSON.stringify(clientData));
    navigate("/dashboard/chat", {
      state: { ...clientData, fromPage: "/dashboard/clientsleadsnuevo" },
    });
  };

  const handleViewLead = (alert) => {
    if (!alert.isRead) {
      markAsRead(alert.id);
    }
    onClose();

    // Alertas de nutricion redirigen a la pantalla de masivos
    if (alert.alertType === "NURTURE_CYCLE" && alert.actionTaken) {
      navigate("/dashboard/setting/massive");
      return;
    }

    const clientState = { id: alert.clientId, name: alert.clientName, cellPhone: alert.clientCellPhone };
    if (location.pathname === "/dashboard/client-info") {
      navigate("/dashboard/home", { replace: true });
      setTimeout(() => {
        navigate("/dashboard/client-info", { state: clientState });
      }, 50);
    } else {
      navigate("/dashboard/client-info", { state: clientState });
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionProps={{ onEnter: handleOpen }}
      PaperProps={{
        sx: {
          width: 380,
          maxHeight: 500,
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Notificaciones
          {(unreadCount > 0 || newMessagesCount > 0 || proposalNotifications?.length > 0 || emailUnreadTotal > 0) && (
            <Chip
              label={(unreadCount || 0) + newMessagesCount + (proposalNotifications?.length || 0) + (emailUnreadTotal || 0)}
              size="small"
              color="error"
              sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Typography>
        {(unreadCount > 0 || newMessagesCount > 0 || proposalNotifications?.length > 0 || emailUnreadTotal > 0) && (
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={markAllRead}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Marcar todas
          </Button>
        )}
      </Box>
      <Divider />

      {/* WhatsApp New Messages Section */}
      {allMessageNotifications.length > 0 && (
        <>
          <Box
            sx={{
              px: 2,
              py: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(37,211,102,0.08)"
                  : "rgba(37,211,102,0.06)",
            }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Mensajes nuevos
            </Typography>
          </Box>
          {allMessageNotifications.map((item) => (
            <Box
              key={item.client.id}
              onClick={() => handleOpenChat(item)}
              sx={{
                p: 1.5,
                px: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: item.isRead
                  ? theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)"
                  : theme.palette.mode === "dark"
                  ? "rgba(37,211,102,0.05)"
                  : "rgba(37,211,102,0.04)",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(37,211,102,0.12)"
                      : "rgba(37,211,102,0.1)",
                  cursor: "pointer",
                },
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                opacity: item.isRead ? 0.55 : 1,
              }}
            >
              <WhatsAppIcon
                sx={{
                  color: item.isRead ? "text.disabled" : "#25d366",
                  fontSize: 20,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={item.isRead ? "normal" : "bold"}
                  noWrap
                  color={item.isRead ? "text.secondary" : "text.primary"}
                >
                  {item.client.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.client.prospect ? "Lead" : "Cliente"}
                </Typography>
              </Box>
              {item.isRead ? (
                <DoneAllIcon sx={{ color: "#25d366", fontSize: 16, flexShrink: 0 }} />
              ) : (
                <Chip
                  label={item.countNewMessages}
                  size="small"
                  sx={{
                    backgroundColor: "#25d366",
                    color: "#fff",
                    fontWeight: "bold",
                    height: 20,
                    fontSize: "0.7rem",
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          ))}
          <Divider />
        </>
      )}

      {/* Email New Messages Section */}
      {emailUnreadTotal > 0 && (
        <>
          <Box
            sx={{
              px: 2,
              py: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(21,101,192,0.08)"
                  : "rgba(21,101,192,0.06)",
            }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Correos nuevos
            </Typography>
          </Box>
          <Box
            onClick={async () => {
              // Mark ALL unread emails as seen immediately so the badge clears
              const clientIds = Object.keys(emailUnreadMap).map(Number).filter(Boolean);
              await Promise.all(clientIds.map((id) => markEmailsSeen(id).catch(() => {})));
              refreshEmailUnread();
              onClose();
              navigate("/dashboard/lead-email", {
                state: { autoSelectLeadId: topUnreadLeadId },
              });
            }}
            sx={{
              p: 1.5,
              px: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(21,101,192,0.05)"
                  : "rgba(21,101,192,0.04)",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(21,101,192,0.12)"
                    : "rgba(21,101,192,0.1)",
                cursor: "pointer",
              },
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <EmailIcon
              sx={{ color: "#1565c0", fontSize: 20, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold" noWrap>
                Correos sin leer
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {emailUnreadTotal} correo{emailUnreadTotal !== 1 ? "s" : ""} sin leer
              </Typography>
            </Box>
            <Chip
              label={emailUnreadTotal}
              size="small"
              sx={{
                backgroundColor: "#1565c0",
                color: "#fff",
                fontWeight: "bold",
                height: 20,
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
            />
          </Box>
          <Divider />
        </>
      )}

      {/* Proposal Completed Notifications */}
      {proposalNotifications?.length > 0 && (
        <>
          <Box
            sx={{
              px: 2,
              py: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(21,118,205,0.08)"
                  : "rgba(21,118,205,0.06)",
            }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Propuestas completadas
            </Typography>
          </Box>
          {proposalNotifications.map((notif) => (
            <Box
              key={notif.id}
              onClick={() => {
                removeProposalNotification(notif.id);
                onClose();
                navigate(`/dashboard/proposal-generator/view/${notif.id}`);
              }}
              sx={{
                p: 1.5,
                px: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(21,118,205,0.05)"
                    : "rgba(21,118,205,0.04)",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(21,118,205,0.12)"
                      : "rgba(21,118,205,0.1)",
                  cursor: "pointer",
                },
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <DescriptionIcon
                sx={{ color: "#1576cd", fontSize: 20, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                  Propuesta completada
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notif.clientCompany || notif.clientName || "Sin nombre"}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  removeProposalNotification(notif.id);
                  onClose();
                  navigate(`/dashboard/proposal-generator/view/${notif.id}`);
                }}
                sx={{
                  textTransform: "none",
                  fontSize: "0.7rem",
                  minWidth: "auto",
                  p: "2px 8px",
                }}
                startIcon={<VisibilityIcon sx={{ fontSize: "14px !important" }} />}
              >
                Ver
              </Button>
            </Box>
          ))}
          <Divider />
        </>
      )}

      {/* Alert List */}
      <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
        {loading && alerts.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : alerts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No hay notificaciones
            </Typography>
          </Box>
        ) : (
          alerts.map((alert) => (
            <Box
              key={alert.id}
              sx={{
                p: 1.5,
                px: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: alert.isRead
                  ? "transparent"
                  : theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(25, 118, 210, 0.04)",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                },
                cursor: "pointer",
              }}
            >
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ mt: 0.3 }}>
                  {getPriorityIcon(alert.priority, alert.alertType)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography
                      variant="body2"
                      fontWeight={alert.isRead ? "normal" : "bold"}
                      noWrap
                      sx={{ flex: 1 }}
                    >
                      {alert.title}
                    </Typography>
                    {!alert.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: getPriorityColor(alert.priority),
                          ml: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {alert.message}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.disabled">
                      {timeAgo(alert.createdAt)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewLead(alert);
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.7rem",
                          minWidth: "auto",
                          p: "2px 8px",
                        }}
                        startIcon={<VisibilityIcon sx={{ fontSize: "14px !important" }} />}
                      >
                        Ver
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          dismiss(alert.id);
                        }}
                        sx={{ p: "2px" }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        )}

        {/* Load More */}
        {page + 1 < totalPages && (
          <Box sx={{ p: 1.5, textAlign: "center" }}>
            <Button
              size="small"
              onClick={loadMore}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              {loading ? <CircularProgress size={16} /> : "Cargar mas"}
            </Button>
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default NotificationPanel;
